import Box from "@mui/material/Box"
import TextField from "@mui/material/TextField"
import React from "react"
import Grid from "@mui/material/Grid"
import { Step4Props } from "../types"
import { Typography, Button, Stack, useMediaQuery, Theme } from "@mui/material"
import { useGtmPageView } from "../../hooks"
import { PackageType } from "../../../../api/reactivationObject/types"
import { AvatarPlayer } from "../../common"

const Step4View: React.FC<Step4Props> = ({ dataProps, onSubmit, isUpdating, note, handleSetNote }) => {
    useGtmPageView({ pageName: "Step4" })
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.between("xs", "md"))
    const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between("md", "lg"))

    const isPortable = isMobile || isTablet

    return (
        <Grid container className="pb-12">
            <Grid container item xs={12} lg={7} spacing={7}>
                <Grid item xs={12}>
                    <Typography className="font-bold text-2xl pb-4 md:text-4xl md:pb-8" variant="h1">
                        Souhrn
                    </Typography>
                    <Typography variant="body1" paragraph className="font-thin mb-0">
                        Máme od Vás vše potřebné, abychom mohli i nadále střežit Váš objekt. Prosíme o finální kontrolu a potvrzení Vámi vyplněných
                        údajů.
                    </Typography>
                    {isPortable && <AvatarPlayer className="lg:hidden" videoName="Summary.mp4" />}
                </Grid>
                <Grid container item spacing={{ xs: "1.25rem", md: 4 }} xs={12}>
                    <Grid item xs={12}>
                        <Typography variant="h2" className="text-xl font-normal pb-5 md:pb-6">
                            Základní informace
                        </Typography>
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    id="name"
                                    fullWidth
                                    aria-readonly
                                    value={dataProps.personName}
                                    label="Jméno a přijmení"
                                    variant="outlined"
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="E-mailová adresa"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.personEmail}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Právní forma"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.personTypeText}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Telefonní číslo"
                                    value={dataProps.personPhoneNumber}
                                    aria-readonly
                                    variant="outlined"
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Druh oprávnění"
                                    variant="outlined"
                                    aria-readonly
                                    value={dataProps.permissionTypeText}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h2" className="text-xl font-normal pb-5 md:pb-6">
                            Identifikace objektu
                        </Typography>
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            <Grid item xs={5} md={3}>
                                <TextField fullWidth label="PSČ" aria-readonly variant="outlined" value={dataProps.objectZIP} disabled />
                            </Grid>
                            <Grid item xs={7} md={4.5}>
                                <TextField fullWidth label="Město" aria-readonly variant="outlined" value={dataProps.objectCity} disabled />
                            </Grid>
                            <Grid item xs={7} md={4.5}>
                                <TextField fullWidth label="Ulice" aria-readonly variant="outlined" value={dataProps.objectStreet} disabled />
                            </Grid>
                            <Grid item xs={5} md={3}>
                                <TextField
                                    fullWidth
                                    label="Č. popisné"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.objectCodeNumber}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={4} md={3}>
                                <TextField
                                    fullWidth
                                    label="Č. orientační"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.objectGuidanceNumber}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={4} md={3}>
                                <TextField
                                    fullWidth
                                    label="Parcelní č."
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.objectParcelNumber}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={4} md={3}>
                                <TextField
                                    fullWidth
                                    label="Evidenční č."
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.objectRegistrationNumber}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h2" className="text-xl font-normal pb-5 md:pb-6">
                            Výběr služby
                        </Typography>
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Vybraná služba"
                                    aria-readonly
                                    variant="outlined"
                                    value={PackageType[dataProps.selectService!]}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Celková cena za měsíc"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.totalCost}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Způsob předání klíčů"
                                    multiline
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.handoverKeysText}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h2" className="text-xl font-normal pb-5 md:pb-6">
                            Fakturační údaje
                        </Typography>
                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            {dataProps.personType === "Legal" && (
                                <>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="IČ" aria-readonly variant="outlined" value={dataProps.ico} disabled />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="DIČ" aria-readonly variant="outlined" value={dataProps.vat} disabled />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Fakturační adresa"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.invoiceAddressFormatted}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Fakturační e-mail"
                                    aria-readonly
                                    variant="outlined"
                                    value={dataProps.emailInvoice}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <hr className="m-0" />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h2" className="text-xl font-normal pb-5 md:pb-6">
                            Poznámka
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            color="primary"
                            multiline
                            InputProps={{
                                className: "border-pantone-yellow-2",
                            }}
                            value={note}
                            inputProps={{
                                className: "p-0",
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#ffcf00",
                                    },
                                },
                            }}
                            placeholder="V případě, že potřebujete zadat doplňující informace nebo upřesnění, využijte prostor zde"
                            onChange={(e) => handleSetNote(e.target.value)}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end" spacing={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            size="large"
                            className="normal-case text-[0.9rem] font-semibold lg:font-normal lg:text-xl shadow-none rounded-md"
                            onClick={() => onSubmit()}
                            disabled={isUpdating}
                        >
                            potvrdit a dokončit
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
            <Grid item xs={0} lg={1} className="hidden lg:block"></Grid>
            <Grid item xs={0} lg={4} className="hidden lg:block">
                {!isPortable && (
                    <Box className="sticky top-32">
                        <AvatarPlayer videoName="Summary.mp4" />
                    </Box>
                )}
            </Grid>
        </Grid>
    )
}

export default Step4View
